import type { ReactNode } from 'react';
import type { LoadStatus } from '@breaker/shared-types';
import { cn } from '@/lib/cn';
import { statusLabel, statusTone, type StatusTone } from '@/lib/status';

const TONE_CLASSES: Record<StatusTone, string> = {
  // Tone palette — neutral/info/active stay muted (out of scope for this
  // phase); success/danger render as solid vivid fills per the approved
  // /theme-preview StatusChip treatment.
  neutral: 'bg-breaker-ground-2 text-breaker-ink-2 border-breaker-line-2',
  /* Quiet washes are alpha over the canon colour — never a Tailwind
     default-palette hex. info keeps its muted blue read via identity
     tokens; active keeps amber with the AA deep text variant. */
  info: 'bg-breaker-identity/10 text-breaker-identity-hover border-breaker-identity/20',
  active: 'bg-breaker-amber/10 text-breaker-amber-deep border-breaker-amber/20',
  success: 'bg-breaker-green text-white border-breaker-green',
  danger: 'bg-breaker-red text-white border-breaker-red',
};

interface BadgeProps {
  tone?: StatusTone;
  children: ReactNode;
  className?: string | undefined;
}

export function Badge({ tone = 'neutral', children, className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 text-2xs font-medium uppercase tracking-wider rounded border transition-colors duration-150 ease-out',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

interface StatusBadgeProps {
  status: LoadStatus;
  className?: string | undefined;
}

export function StatusBadge({ status, className }: StatusBadgeProps) {
  return (
    <Badge tone={statusTone(status)} className={className}>
      {statusLabel(status)}
    </Badge>
  );
}
